import { clamp, round0 } from './formatters'

export type PackageType = '' | 'standard'
export type BuildPayType = '' | 'standard'
export type IndustryType =
  | ''
  | 'Windows'
  | 'Roofing'
  | 'Solar (Sales)'
  | 'Solar (Vertical)'
  | 'Solar (Community)'
  | 'Pest'
  | 'Fiber'
  | 'Deregulated Energy'
  | 'Hardscaping'
  | 'Landscaping'
  | 'Other'

export interface Inputs {
  companyName: string
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  pkg: PackageType
  buildPay: BuildPayType
  industry: IndustryType
  campaigns: number
  monthlyCampaigns?: number[]
  adSpend: number
  monthlyAdSpend?: number[]
  cph: number
  recruiterHourlyRate: number
  profitPerCustomer: number
  salesVetMonthly: number
  salesFirst90Total: number
  pctLaunched: number
  pct30: number
  pct60: number
  pct90: number
  pct1y: number
  pullthrough: number
  recruitingMonths?: number
  // Legacy fields (kept for backwards compatibility)
  ticket?: number
  gpm?: number
}

export interface IndustryDefaults {
  profitPerCustomer: number
  salesFirst90Total: number
  salesVetMonthly: number
}

export const INDUSTRY_DEFAULTS: Record<IndustryType, IndustryDefaults> = {
  '': {
    profitPerCustomer: 5000,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  Windows: {
    profitPerCustomer: 6000,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  Roofing: {
    profitPerCustomer: 4000,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  'Solar (Sales)': {
    profitPerCustomer: 4000,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
  },
  'Solar (Vertical)': {
    profitPerCustomer: 8000,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
  },
  'Solar (Community)': {
    profitPerCustomer: 175,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  Pest: {
    profitPerCustomer: 340,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  Fiber: {
    profitPerCustomer: 200,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  'Deregulated Energy': {
    profitPerCustomer: 175,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  Hardscaping: {
    profitPerCustomer: 5000,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  Landscaping: {
    profitPerCustomer: 15000,
    salesFirst90Total: 5,
    salesVetMonthly: 8,
  },
  Other: {
    profitPerCustomer: 5000,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
}

export interface PackagePreset {
  name: string
  buildTwoPayments: number
  buildUpfront: number
  systemMonthly: number
  campaignMonthly: number
  bonusLaunch: number
  bonusDay30: number
  bonusDay60: number
  bonusDay90: number
}

export const PACKAGE_PRESETS: Record<PackageType, PackagePreset> = {
  '': {
    name: '',
    buildTwoPayments: 0,
    buildUpfront: 0,
    systemMonthly: 0,
    campaignMonthly: 0,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  standard: {
    name: 'Standard Recruiting Model',
    buildTwoPayments: 0,
    buildUpfront: 0,
    systemMonthly: 0,
    campaignMonthly: 0,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
}

export interface RowData {
  period: string
  activeCampaigns: number
  hires: number
  launched: number
  active30d: number
  active60d: number
  active90d: number
  veterans: number
  buildFee: number
  platformFee: number
  campaignFee: number
  hireCost: number
  adSpend: number
  bonuses: number
  totalCost: number
  totalSales: number
  realizationRate: number
  grossRevenue: number
  grossProfit: number
  netProfit: number
  cumProfit: number
}

export interface CalculationResult {
  preset: PackagePreset
  hiresPerMonth: number
  launchedPerMonth: number
  gpPerSale: number
  totalHireCost: number
  totalAdSpend: number
  totalBonuses: number
  totalCostAll: number
  totalGrossProfit: number
  totalNetProfit: number
  roi: number
  breakeven: string
  rows: RowData[]
}

export const DEFAULT_INPUTS: Inputs = {
  companyName: '',
  email: '',
  pkg: 'standard',
  buildPay: 'standard',
  industry: 'Roofing',
  campaigns: 1,
  monthlyCampaigns: Array(12).fill(1),
  adSpend: 4000,
  monthlyAdSpend: Array(12).fill(4000),
  cph: 350,
  recruiterHourlyRate: 50,
  profitPerCustomer: 4000,
  salesVetMonthly: 6,
  salesFirst90Total: 10,
  pctLaunched: 70,
  pct30: 30,
  pct60: 60,
  pct90: 80,
  pct1y: 30,
  pullthrough: 80,
  recruitingMonths: 3,
}

export function calculateModel(inputs: Inputs): CalculationResult {
  const months = 12
  const preset = PACKAGE_PRESETS[inputs.pkg] || PACKAGE_PRESETS.standard

  const profitPerCustomer = Math.max(0, inputs.profitPerCustomer)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pct30 = clamp(inputs.pct30 / 100, 0, 1)
  const pct60 = clamp(inputs.pct60 / 100, 0, 1)
  const pct90 = clamp(inputs.pct90 / 100, 0, 1)
  const pct1y = clamp(inputs.pct1y / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  const monthlyCampaigns =
    inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12
      ? inputs.monthlyCampaigns
      : Array(12).fill(Math.max(1, inputs.campaigns))

  const monthlyAdSpendValues =
    inputs.monthlyAdSpend && inputs.monthlyAdSpend.length === 12
      ? inputs.monthlyAdSpend
      : Array(12).fill(Math.max(0, inputs.adSpend))

  const cph = Math.max(1, inputs.cph)
  const recruiterHourlyRate = Math.max(0, inputs.recruiterHourlyRate)
  const hoursPerRecruit = 5

  const hired = monthlyCampaigns.map((campaigns, idx) => {
    const adSpendPerCampaign = monthlyAdSpendValues[idx]
    const totalAdSpend = adSpendPerCampaign * campaigns
    return round0(totalAdSpend / cph)
  })

  const recruitingTimeCost = hired.map((hires) => {
    return hires * hoursPerRecruit * recruiterHourlyRate
  })

  const launched = hired.map((h) => h * pctLaunched)
  const cohortDay30 = launched.map((x) => x * pct30)
  const cohortDay60 = cohortDay30.map((x) => x * pct60)
  const cohortDay90 = cohortDay60.map((x) => x * pct90)

  const active30d = Array.from({ length: months }, (_, m) => {
    return m >= 1 ? cohortDay30[m - 1] : 0
  })

  const active60d = Array.from({ length: months }, (_, m) => {
    return m >= 2 ? cohortDay60[m - 2] : 0
  })

  const active90d = Array.from({ length: months }, (_, m) => {
    return m >= 3 ? cohortDay90[m - 3] : 0
  })

  const monthlyVeteranRetentionRate = Math.pow(pct1y, 1 / 9)

  const veterans: number[] = []
  for (let m = 0; m < months; m++) {
    if (m <= 3) {
      veterans.push(0)
    } else {
      const previousVeterans = veterans[m - 1] * monthlyVeteranRetentionRate
      const newGraduates = m >= 4 ? cohortDay90[m - 4] : 0
      veterans.push(previousVeterans + newGraduates)
    }
  }

  const salesPer30Days = Math.max(0, inputs.salesFirst90Total) / 3

  const totalSales = Array.from({ length: months }, (_, m) => {
    let sales = 0

    if (m >= 0) {
      const eventualGrads = cohortDay90[m]
      sales += eventualGrads * salesPer30Days
    }

    if (m >= 1) {
      const eventualGrads = cohortDay90[m - 1]
      sales += eventualGrads * salesPer30Days
    }

    if (m >= 2) {
      const eventualGrads = cohortDay90[m - 2]
      sales += eventualGrads * salesPer30Days
    }

    if (m >= 3) {
      for (let cohortMonth = 0; cohortMonth <= m - 3; cohortMonth++) {
        let veteranCount = cohortDay90[cohortMonth]
        const monthsPast90 = m - cohortMonth - 3
        for (let ageMonth = 0; ageMonth < monthsPast90; ageMonth++) {
          veteranCount *= monthlyVeteranRetentionRate
        }
        sales += veteranCount * Math.max(0, inputs.salesVetMonthly)
      }
    }

    return sales
  })

  const realizedSales = totalSales.map((x) => x * pullthrough)

  // Gross profit is sales * profit per customer
  const grossProfit = realizedSales.map((sales) => sales * profitPerCustomer)
  const grossRevenue = grossProfit.map((profit) => profit) // For display purposes, revenue = profit
  const gpPerSale = profitPerCustomer

  const buildLaunch = 0
  const buildFees = Array(months).fill(0)
  const platformFees = Array(months).fill(0)
  const campaignFees = Array(months).fill(0)
  const bonuses = Array(months).fill(0)
  const hireCost = Array(months).fill(0)

  const adSpend = monthlyCampaigns.map(
    (c, idx) => monthlyAdSpendValues[idx] * c,
  )

  const totalCost = Array(months)
    .fill(0)
    .map((_, i) => adSpend[i] + recruitingTimeCost[i])

  const netProfit = grossProfit.map((x, i) => x - totalCost[i])

  const rows: RowData[] = []
  let cum = 0

  rows.push({
    period: 'Launch',
    activeCampaigns: 0,
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    buildFee: 0,
    platformFee: 0,
    campaignFee: 0,
    hireCost: 0,
    adSpend: 0,
    bonuses: 0,
    totalCost: 0,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: 0,
    cumProfit: cum,
  })

  for (let i = 0; i < months; i++) {
    cum += netProfit[i]
    rows.push({
      period: `Month ${i + 1}`,
      activeCampaigns: monthlyCampaigns[i],
      hires: hired[i],
      launched: launched[i],
      active30d: active30d[i],
      active60d: active60d[i],
      active90d: active90d[i],
      veterans: veterans[i],
      buildFee: 0,
      platformFee: 0,
      campaignFee: 0,
      hireCost: recruitingTimeCost[i],
      adSpend: adSpend[i],
      bonuses: 0,
      totalCost: totalCost[i],
      totalSales: totalSales[i],
      realizationRate: pullthrough,
      grossRevenue: grossRevenue[i],
      grossProfit: grossProfit[i],
      netProfit: netProfit[i],
      cumProfit: cum,
    })
  }

  const totalHireCost = recruitingTimeCost.reduce((a, b) => a + b, 0)
  const totalAdSpend = adSpend.reduce((a, b) => a + b, 0)
  const totalBonuses = 0
  const totalCostAll = totalAdSpend + totalHireCost
  const totalGrossProfit = grossProfit.reduce((a, b) => a + b, 0)
  const totalNetProfit = totalGrossProfit - totalCostAll
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = 'Not within 12 months'
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  return {
    preset,
    hiresPerMonth: hired[0],
    launchedPerMonth: launched[0],
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}

export function calculateSummerModel(inputs: Inputs): CalculationResult {
  const preset = PACKAGE_PRESETS[inputs.pkg] || PACKAGE_PRESETS.standard

  const recruitingMonths = inputs.recruitingMonths || 3
  const summerMonths = 5
  const totalMonths = recruitingMonths + summerMonths

  const profitPerCustomer = Math.max(0, inputs.profitPerCustomer)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  const cph = Math.max(1, inputs.cph)
  const recruiterHourlyRate = Math.max(0, inputs.recruiterHourlyRate)
  const hoursPerRecruit = 5
  const adSpendPerMonth = Math.max(0, inputs.adSpend)

  const hiresPerMonth = round0(adSpendPerMonth / cph)
  const totalHires = hiresPerMonth * recruitingMonths
  const totalLaunched = round0(totalHires * pctLaunched)

  const summerSalesPerRep = Math.max(0, inputs.salesFirst90Total)

  const rows: RowData[] = []
  let cum = 0

  rows.push({
    period: 'Launch',
    activeCampaigns: 0,
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    buildFee: 0,
    platformFee: 0,
    campaignFee: 0,
    hireCost: 0,
    adSpend: 0,
    bonuses: 0,
    totalCost: 0,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: 0,
    cumProfit: cum,
  })

  for (let m = 0; m < recruitingMonths; m++) {
    const monthHires = hiresPerMonth
    const monthLaunched = 0
    const recruitingCost = monthHires * hoursPerRecruit * recruiterHourlyRate
    const monthAdSpend = adSpendPerMonth
    const totalCost = recruitingCost + monthAdSpend

    cum += -totalCost

    rows.push({
      period: `Month ${m + 1}`,
      activeCampaigns: 1,
      hires: monthHires,
      launched: monthLaunched,
      active30d: 0,
      active60d: 0,
      active90d: 0,
      veterans: 0,
      buildFee: 0,
      platformFee: 0,
      campaignFee: 0,
      hireCost: recruitingCost,
      adSpend: monthAdSpend,
      bonuses: 0,
      totalCost: totalCost,
      totalSales: 0,
      realizationRate: pullthrough,
      grossRevenue: 0,
      grossProfit: 0,
      netProfit: -totalCost,
      cumProfit: cum,
    })
  }

  const salesPerSummerMonth = summerSalesPerRep / summerMonths

  for (let m = 0; m < summerMonths; m++) {
    const monthIndex = recruitingMonths + m
    const activeReps = totalLaunched
    const monthSales = activeReps * salesPerSummerMonth
    const realizedSales = monthSales * pullthrough

    const grossProfit = realizedSales * profitPerCustomer
    const grossRevenue = grossProfit
    const totalCost = 0
    const netProfit = grossProfit - totalCost

    cum += netProfit

    rows.push({
      period: `Month ${monthIndex + 1}`,
      activeCampaigns: 0,
      hires: 0,
      launched: m === 0 ? totalLaunched : 0,
      active30d: activeReps,
      active60d: 0,
      active90d: 0,
      veterans: 0,
      buildFee: 0,
      platformFee: 0,
      campaignFee: 0,
      hireCost: 0,
      adSpend: 0,
      bonuses: 0,
      totalCost: totalCost,
      totalSales: monthSales,
      realizationRate: pullthrough,
      grossRevenue: grossRevenue,
      grossProfit: grossProfit,
      netProfit: netProfit,
      cumProfit: cum,
    })
  }

  const totalHireCost =
    hiresPerMonth * recruitingMonths * hoursPerRecruit * recruiterHourlyRate
  const totalAdSpend = adSpendPerMonth * recruitingMonths
  const totalBonuses = 0
  const totalCostAll = totalAdSpend + totalHireCost

  const totalGrossProfit = rows
    .slice(1)
    .reduce((sum, row) => sum + row.grossProfit, 0)
  const totalNetProfit = totalGrossProfit - totalCostAll
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = 'Not within season'
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  const gpPerSale = profitPerCustomer

  return {
    preset,
    hiresPerMonth,
    launchedPerMonth: totalLaunched / recruitingMonths,
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}
